import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, Info, Check } from 'lucide-react';
import { CountrySelector } from '../settings/CountrySelector';
export interface Registration {
  registrationId: string;
  identifier: string;
  schemeCode?: string;
  issuer?: string;
  countryId?: string;
  country?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  isActive: boolean;
  createdAt: string;
  createdById: string;
  updatedAt?: string;
  updatedById?: string;
  attributes?: any[];
}
interface VendorRegistrationFormProps {
  registration?: Registration;
  onSave: (registration: Registration) => void;
  onCancel: () => void;
  locale: string;
}
export function VendorRegistrationForm({
  registration,
  onSave,
  onCancel,
  locale
}: VendorRegistrationFormProps) {
  const [formData, setFormData] = useState<Partial<Registration>>({
    registrationId: '',
    identifier: '',
    schemeCode: '',
    issuer: '',
    countryId: '',
    country: '',
    effectiveFrom: '',
    effectiveTo: '',
    isActive: true,
    createdAt: new Date().toISOString(),
    createdById: 'user-current',
    attributes: []
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Common scheme codes
  const schemeCodes = [{
    code: 'ABN',
    label: 'Australian Business Number (ABN)'
  }, {
    code: 'ACN',
    label: 'Australian Company Number (ACN)'
  }, {
    code: 'NZBN',
    label: 'New Zealand Business Number (NZBN)'
  }, {
    code: 'VAT',
    label: 'Value Added Tax (VAT)'
  }, {
    code: 'EIN',
    label: 'Employer Identification Number (EIN)'
  }, {
    code: 'GST',
    label: 'Goods and Services Tax (GST)'
  }, {
    code: 'UEN',
    label: 'Unique Entity Number (UEN)'
  }, {
    code: 'DUNS',
    label: 'Data Universal Numbering System (DUNS)'
  }, {
    code: 'GLN',
    label: 'Global Location Number (GLN)'
  }];
  useEffect(() => {
    if (registration) {
      setFormData({
        ...registration,
        effectiveFrom: registration.effectiveFrom ? registration.effectiveFrom.split('T')[0] : '',
        effectiveTo: registration.effectiveTo ? registration.effectiveTo.split('T')[0] : ''
      });
    } else {
      setFormData({
        registrationId: `reg-${Date.now()}`,
        identifier: '',
        schemeCode: '',
        issuer: '',
        countryId: '',
        country: '',
        effectiveFrom: '',
        effectiveTo: '',
        isActive: true,
        createdAt: new Date().toISOString(),
        createdById: 'user-current',
        attributes: []
      });
    }
  }, [registration]);
  // Get current attributes (non-expired)
  const currentAttributes = formData.attributes?.filter((attr: any) => {
    if (!attr.effectiveTo) return true;
    return new Date(attr.effectiveTo) > new Date();
  }) || [];
  const formatAttributeValue = (val: string, type: string | null) => {
    if (type === 'boolean') {
      return val === 'true' ? <span className="inline-flex items-center text-green-700" data-id="element-22812">
          <Check className="h-4 w-4 mr-1" data-id="element-22813" /> Yes
        </span> : <span className="text-gray-600" data-id="element-22814">No</span>;
    }
    if (type === 'date') {
      return new Date(val).toLocaleDateString();
    }
    return val;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handleCountryChange = (countryCode: string) => {
    setFormData({
      ...formData,
      country: countryCode,
      // In a real app, we'd map code to ID
      countryId: countryCode ? `country-${countryCode}` : undefined
    });
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.identifier?.trim()) {
      newErrors.identifier = 'Identifier is required';
    }
    if (formData.effectiveFrom && formData.effectiveTo) {
      if (new Date(formData.effectiveFrom) > new Date(formData.effectiveTo)) {
        newErrors.effectiveTo = 'End date must be after start date';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData as Registration);
    }
  };
  return <div className="bg-white shadow sm:rounded-lg" data-id="element-22815">
      <div className="px-4 py-5 sm:p-6" data-id="element-22816">
        <div className="flex justify-between items-center mb-5" data-id="element-22817">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-22818">
            {registration ? 'Edit Registration' : 'Add Registration'}
          </h3>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-500" data-id="element-22819">
            <XIcon className="h-5 w-5" data-id="element-22820" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-22821">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-22822">
            <div className="sm:col-span-3" data-id="element-22823">
              <label htmlFor="identifier" className="block text-sm font-medium text-gray-700" data-id="element-22824">
                Identifier <span className="text-red-500" data-id="element-22825">*</span>
              </label>
              <div className="mt-1" data-id="element-22826">
                <input type="text" name="identifier" id="identifier" value={formData.identifier} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.identifier ? 'border-red-300' : ''}`} placeholder="e.g. 51824753556" data-id="element-22827" />
                {errors.identifier && <p className="mt-1 text-sm text-red-600" data-id="element-22828">
                    {errors.identifier}
                  </p>}
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-22829">
              <label htmlFor="schemeCode" className="block text-sm font-medium text-gray-700" data-id="element-22830">
                Scheme / Type
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-22831">
                <input type="text" name="schemeCode" id="schemeCode" list="schemeCodes" value={formData.schemeCode} onChange={handleChange} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="e.g. ABN, VAT" data-id="element-22832" />
                <datalist id="schemeCodes" data-id="element-22833">
                  {schemeCodes.map(scheme => <option key={scheme.code} value={scheme.code} data-id="element-22834">
                      {scheme.label}
                    </option>)}
                </datalist>
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-22835">
              <label htmlFor="issuer" className="block text-sm font-medium text-gray-700" data-id="element-22836">
                Issuer / Authority
              </label>
              <div className="mt-1" data-id="element-22837">
                <input type="text" name="issuer" id="issuer" value={formData.issuer} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="e.g. ASIC, Companies Office" data-id="element-22838" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-22839">
              <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-22840">
                Country Context
              </label>
              <div className="mt-1" data-id="element-22841">
                <CountrySelector value={formData.country || ''} onChange={handleCountryChange} locale={locale} data-id="element-22842" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-22843">
              <label htmlFor="effectiveFrom" className="block text-sm font-medium text-gray-700" data-id="element-22844">
                Effective From
              </label>
              <div className="mt-1" data-id="element-22845">
                <input type="date" name="effectiveFrom" id="effectiveFrom" value={formData.effectiveFrom} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-22846" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-22847">
              <label htmlFor="effectiveTo" className="block text-sm font-medium text-gray-700" data-id="element-22848">
                Effective To
              </label>
              <div className="mt-1" data-id="element-22849">
                <input type="date" name="effectiveTo" id="effectiveTo" value={formData.effectiveTo} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.effectiveTo ? 'border-red-300' : ''}`} data-id="element-22850" />
                {errors.effectiveTo && <p className="mt-1 text-sm text-red-600" data-id="element-22851">
                    {errors.effectiveTo}
                  </p>}
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-22852">
              <div className="flex items-start" data-id="element-22853">
                <div className="flex items-center h-5" data-id="element-22854">
                  <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-22855" />
                </div>
                <div className="ml-3 text-sm" data-id="element-22856">
                  <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-22857">
                    Active Registration
                  </label>
                  <p className="text-gray-500" data-id="element-22858">
                    Uncheck if this registration is no longer valid or in use.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Registration Attributes Section */}
          {currentAttributes.length > 0 && <div className="pt-6 border-t border-gray-200" data-id="element-22859">
              <div className="flex items-center mb-3" data-id="element-22860">
                <Info className="h-5 w-5 text-blue-500 mr-2" data-id="element-22861" />
                <h4 className="text-sm font-medium text-gray-900" data-id="element-22862">
                  Registry Attributes from {formData.issuer || 'Registry'}
                </h4>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-64 overflow-y-auto" data-id="element-22863">
                {currentAttributes.map((attr: any) => <div key={attr.registrationAttributeId} className="flex justify-between items-start py-2 border-b border-gray-200 last:border-0" data-id="element-22864">
                    <div className="flex-1" data-id="element-22865">
                      <span className="text-sm font-medium text-gray-700" data-id="element-22866">
                        {attr.attributeKey}
                      </span>
                      <div className="text-xs text-gray-500 mt-0.5" data-id="element-22867">
                        Source: {attr.source}
                        {attr.retrievedAt && <span className="ml-2" data-id="element-22868">
                            • Retrieved:{' '}
                            {new Date(attr.retrievedAt).toLocaleDateString()}
                          </span>}
                      </div>
                    </div>
                    <div className="text-sm text-gray-900 ml-4 text-right" data-id="element-22869">
                      {formatAttributeValue(attr.attributeValue, attr.valueType)}
                    </div>
                  </div>)}
              </div>
              <p className="mt-2 text-xs text-gray-500 italic" data-id="element-22870">
                Note: Attributes are managed separately. To edit attributes,
                view the full registration details.
              </p>
            </div>}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200" data-id="element-22871">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22872">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22873">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-22874" />
              Save Registration
            </button>
          </div>
        </form>
      </div>
    </div>;
}